/* eslint-disable max-lines-per-function */
import { useCallback } from 'react';

import { AppointmentId } from '@dreryk/egabinet-appointment-published-language-api';
import {
   VisitAlreadySettledFault,
   VisitSettlementUpdateMedicalServicesCommand,
} from '@dreryk/egabinet-financial-settlement-frontend-api';
import { PurchaseId } from '@dreryk/egabinet-financial-settlement-published-language-api';

import { useCqrsCommand } from '@dreryk/command-query-react-common';
import { dispatchSnackbarNegativeEvent, dispatchSnackbarPositiveEvent } from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';
import { PromiseVoidFunction } from '@dreryk/react-common';

import { visitSettlementTranslationKeys } from '../../translations';
import { VisitSettlementControl } from '../../visitSettlement.types';
import { getVisitSettlementValue } from '../../visitSettlement.utils';
import { useRefetchVisitSettlement } from '../useRefetchVisitSettlement';

import { medicalServicesToVisitMedicalServiceParameters, triggerAllMedicalServices } from './medicalServices.utils';

const t = visitSettlementTranslationKeys.shared.medicalServices.updateInterceptor;

type VisitSettlementUpdateMedicalServicesCommandData = Pick<
   VisitSettlementUpdateMedicalServicesCommand,
   'medicalServiceParameters' | 'priceChangeReason'
>;

type UseVisitSettlementUpdateMedicalServicesCommandOptions = {
   control: VisitSettlementControl;
   appointmentId: number;
   blockingOperation?: boolean;
};

export const useVisitSettlementUpdateMedicalServicesCommand = (
   options: UseVisitSettlementUpdateMedicalServicesCommandOptions
): PromiseVoidFunction => {
   const refetchVisitSettlement = useRefetchVisitSettlement();

   const { executeAsync: updateVisitMedicalServices } = useCqrsCommand<
      void,
      VisitSettlementUpdateMedicalServicesCommandData,
      VisitAlreadySettledFault
   >(
      (parameters) => {
         const command = new VisitSettlementUpdateMedicalServicesCommand();
         command.purchaseId = PurchaseId.forAppointment(AppointmentId.of(options.appointmentId));
         command.priceChangeReason = parameters.priceChangeReason;
         command.medicalServiceParameters = parameters.medicalServiceParameters;
         return command;
      },
      {
         onSuccess: () => {
            if (!options.blockingOperation) {
               dispatchSnackbarPositiveEvent(t.successfully);
            }
         },
         onFault: {
            'egabinet-financial-settlement-frontend-api:VisitAlreadySettledFault': () => {
               refetchVisitSettlement();
               dispatchSnackbarNegativeEvent(t.errors.visitAlreadySettledFault);
            },
         },
         onUnexpectedError: () => {
            dispatchSnackbarNegativeEvent(t.errors.unexpectedError.blockingOperation);
         },
      }
   );

   // eslint-disable-next-line @typescript-eslint/require-await
   return useCallback(async (): Promise<void> => {
      const visitSettlement = getVisitSettlementValue(options.control);
      const medicalServicesValid = visitSettlement.editable && (await triggerAllMedicalServices(options.control));

      return new Promise<void>((resolve, reject) => {
         if (!visitSettlement) {
            if (options.blockingOperation) {
               dispatchSnackbarNegativeEvent(t.errors.unexpectedError.blockingOperation);

               reject(new Error('Invalid medical services.'));
               return;
            }

            dispatchSnackbarNegativeEvent(t.errors.unexpectedError.noBlockingOperation);
            resolve();
            return;
         }

         if (!visitSettlement.editable) {
            resolve();
            return;
         }

         if (!medicalServicesValid) {
            dispatchSnackbarNegativeEvent(t.errors.invalidMedicalServices);

            reject(new Error('Invalid medical services.'));
            return;
         }

         updateVisitMedicalServices({
            priceChangeReason: Choice.withUseDefaultIfNull(visitSettlement.priceChangeReason),
            medicalServiceParameters: medicalServicesToVisitMedicalServiceParameters(visitSettlement.medicalServices),
         })
            .then(async () => {
               if (!options.blockingOperation) {
                  await refetchVisitSettlement();
               }

               return resolve();
            })
            .catch(reject);
      });
   }, [options.control, options.blockingOperation, updateVisitMedicalServices, refetchVisitSettlement]);
};
